/**
 * @file components/log/weightCharts/chartUtils.js
 * @description Pure data transforms + shared recharts styling for the
 * weight-progress analytics charts.
 */

// ── Shared recharts styling (spread onto axes/grid/chart) ───────────────────
export const CHART_MARGIN = { top: 10, right: 10, left: -25, bottom: 0 };
export const GRID_PROPS = {
  strokeDasharray: "3 3",
  stroke: "#ffffff05",
  vertical: false,
};
const AXIS_TICK = { fill: "#666", fontSize: 10 };
export const X_AXIS_PROPS = {
  axisLine: false,
  tickLine: false,
  tick: AXIS_TICK,
  dy: 10,
};
export const Y_AXIS_PROPS = {
  axisLine: false,
  tickLine: false,
  tick: AXIS_TICK,
};

/** Handle both JS Date and Firebase Timestamps. */
export const parseDate = (date) => {
  if (!date) return new Date();
  return date.seconds ? new Date(date.seconds * 1000) : new Date(date);
};

function getDailyCalorieTotals(meals) {
  const map = {};
  meals?.forEach((meal) => {
    const d = parseDate(meal.createdAt);
    const dateStr = d.toISOString().split("T")[0];
    map[dateStr] = (map[dateStr] || 0) + (meal.calories || 0);
  });
  return map;
}

export function mergeChartData(weightLogs, meals, profileData) {
  const dailyCals = getDailyCalorieTotals(meals);
  // Using 1945 as seen in your dashboard screenshot for accuracy
  const targetCalories = profileData.targetCalories || 1945;

  const data = weightLogs.map((log) => {
    const d = parseDate(log.date || log.createdAt);
    const dateStr = d.toISOString().split("T")[0];
    const eaten = dailyCals[dateStr] || 0;

    return {
      date: dateStr,
      dateDisplay: d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      weight: Number(log.weight),
      calories: eaten,
      target: targetCalories,
      goalWeight: Number(profileData.goalWeight),
    };
  });

  return data.sort((a, b) => new Date(a.date) - new Date(b.date));
}

export function calculateTrendLine(data) {
  if (data.length < 2) return data;
  const n = data.length;
  const xs = data.map((_, i) => i);
  const ys = data.map((d) => d.weight);
  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((sum, x, i) => sum + x * ys[i], 0);
  const sumX2 = xs.reduce((sum, x) => sum + x * x, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return data.map((d, i) => ({ ...d, trend: slope * i + intercept }));
}
